/**
 * Browser persistence for the WP2 environment preference.
 * localStorage only — no migration, no synced column. Browser storage is
 * best-effort: denied access, getItem failures, quota errors, and disabled
 * storage degrade to the real runtime instead of crashing the verification shell.
 */

import {
  clearPersistedEnvironment,
  type EnvPreferenceState,
  type EnvWorkspaceId,
  VERIFICATION_ENV_STORAGE_KEY,
  type EnvPreferenceStore,
  type VerificationEnvironment,
  markPersistedEnvironmentCleared,
  readPersistedEnvironment,
  readPersistedEnvironmentState,
  writePersistedEnvironment,
} from "./env";

export function browserEnvStore(): EnvPreferenceStore | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function loadEnvironmentPreference(workspaceId?: EnvWorkspaceId): VerificationEnvironment | null {
  const store = browserEnvStore();
  if (!store) return null;
  try {
    return readPersistedEnvironment(store, workspaceId);
  } catch {
    return null;
  }
}

/**
 * Three-way read of the scoped preference. An unreadable store reports `absent`,
 * which adopts the real runtime rather than crashing the verification shell.
 */
export function loadEnvironmentPreferenceState(workspaceId?: EnvWorkspaceId): EnvPreferenceState {
  const store = browserEnvStore();
  if (!store) return { kind: "absent" };
  try {
    return readPersistedEnvironmentState(store, workspaceId);
  } catch {
    return { kind: "absent" };
  }
}

export function loadLegacyEnvironmentPreference(): VerificationEnvironment | null {
  const store = browserEnvStore();
  if (!store) return null;
  try {
    return readPersistedEnvironment(store);
  } catch {
    return null;
  }
}

export function saveEnvironmentPreference(
  environment: VerificationEnvironment,
  workspaceId?: EnvWorkspaceId,
): void {
  const store = browserEnvStore();
  if (!store) return;
  try {
    writePersistedEnvironment(store, environment, workspaceId);
  } catch {
    /* quota / disabled */
  }
}

/**
 * Settings reset. Drops every stored choice, then records that the person asked
 * to be asked again — otherwise the next reload would read an absent key and
 * silently re-adopt This Mac, and the reset would have no visible effect.
 */
export function markEnvironmentPreferenceCleared(workspaceId?: EnvWorkspaceId): void {
  clearEnvironmentPreference(workspaceId);
  const store = browserEnvStore();
  if (!store) return;
  try {
    markPersistedEnvironmentCleared(store, workspaceId);
  } catch {
    /* quota / disabled */
  }
}

export function clearEnvironmentPreference(workspaceId?: EnvWorkspaceId): void {
  const store = browserEnvStore();
  if (!store) return;
  try {
    clearPersistedEnvironment(store, workspaceId);
    clearPersistedEnvironment(store);
    if ("length" in store && "key" in store) {
      const storage = store as Storage;
      for (let i = storage.length - 1; i >= 0; i--) {
        const key = storage.key(i);
        if (key?.startsWith(`${VERIFICATION_ENV_STORAGE_KEY}:`)) storage.removeItem(key);
      }
    }
  } catch {
    /* disabled */
  }
}
